/**
 * Reine Anzeige-Formatter für die Player-Intel-Detailansicht. Keine Datenlogik,
 * kein Server-Import außer den vorhandenen (UI-sicheren) Vehicle-Display-Names.
 * Unbekanntes wird nur „hübsch formatiert" — NIE falsch behauptet.
 */
import { resolveVehicleDisplayName } from "../game/bf2142-display-names";

export const DASH = "—";

function isEmptyRaw(raw: string | null | undefined): boolean {
  const v = String(raw ?? "").trim().toLowerCase();
  return v === "" || v === "none" || v === "null" || v === "0";
}

/** underscores/dashes → spaces, camelCase splitten, Whitespace normalisieren. */
function prettyWords(s: string): string {
  return s
    .replace(/[_\-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
}

/** Title Case; bekannte Faktions-Token bleiben groß (PAC/EU/US/AS). */
function titleCase(s: string): string {
  return s
    .split(" ")
    .filter(Boolean)
    .map((w) => {
      const lw = w.toLowerCase();
      if (lw === "pac" || lw === "eu" || lw === "us" || lw === "as") return lw.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
}

/** Team-Rohwert → `PAC` / `EU` / `—`. bf2cc: 1=PAC, 2=EU (us=EU, as=PAC im Engine-Code). */
export function formatTeam(raw: string | null | undefined): string {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "" || v === "0" || v === "none") return DASH;
  if (v === "1" || v === "pac" || v === "as") return "PAC";
  if (v === "2" || v === "eu" || v === "us") return "EU";
  return String(raw).toUpperCase();
}

/** Tönungs-Klasse fürs Team-Badge (pac/eu/leer). */
export function teamTone(raw: string | null | undefined): "pac" | "eu" | "" {
  const f = formatTeam(raw);
  return f === "PAC" ? "pac" : f === "EU" ? "eu" : "";
}

/** Kit-Code → lesbar; Faktion bleibt als Prefix erhalten. `Pac_Assault` → `PAC Assault`. */
export function formatKitName(raw: string | null | undefined): string {
  if (isEmptyRaw(raw)) return DASH;
  return titleCase(prettyWords(String(raw))) || DASH;
}

const FACTION_PREFIX = /^(pac|eu|us|as)[_\s]+/i;
const KNOWN_VEHICLE: Record<string, string> = {
  multiplayerfreecamera: "Free Camera",
  freecamera: "Free Camera",
};

/**
 * Vehicle-/Soldier-Code → lesbar. Reihenfolge:
 * 1) bekannte BF2142-Display-Names (`us_ag` → `UD-6 Talon`),
 * 2) bekannte Spezialwerte (`MultiPlayerFreeCamera` → `Free Camera`),
 * 3) Fallback: Faktions-Prefix droppen + hübsch (`pac_heavy_soldier` → `Heavy Soldier`).
 */
export function formatVehicleName(raw: string | null | undefined, team: 0 | 1 | 2 = 0): string {
  if (isEmptyRaw(raw)) return DASH;
  const v = String(raw).trim();
  // Nur ein NICHT-leerer Display-Name gewinnt (echtes Fahrzeug, z.B. UD-6 Talon).
  // `""` = im Mapping bewusst „nichts" (Soldat/zu Fuß) → hier prettifien wir den
  // Soldaten-Code (Spec: `pac_heavy_soldier` → `Heavy Soldier`).
  const disp = resolveVehicleDisplayName(v, team);
  if (disp) return disp;
  const known = KNOWN_VEHICLE[v.toLowerCase()];
  if (known) return known;
  return titleCase(prettyWords(v.replace(FACTION_PREFIX, ""))) || DASH;
}

/** Provider/Org → getrimmt, leer → `—` (langes darf in der UI umbrechen). */
export function formatProvider(raw: string | null | undefined): string {
  const v = String(raw ?? "").trim();
  return v === "" ? DASH : v;
}

/** Seen-Count → robuste, locale-unabhängige Zahl (≥0). */
export function formatSeenCount(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return "0";
  return String(Math.floor(v));
}

/**
 * ClanTag als Identitäts-Pill VOR dem Namen: getrimmter Tag oder `null`, wenn
 * keiner vorhanden ist (dann KEIN „KEIN TAG"-Badge in der Headline). Reiner
 * Anzeige-Helfer — kein Parsing, der Tag kommt unverändert aus der History.
 */
export function clanTagPill(raw: string | null | undefined): string | null {
  const v = String(raw ?? "").trim();
  return v === "" ? null : v;
}
